import { Button, Dialog, Spinner } from '@economic/taco';
import { Trans } from '@lingui/macro';
import qs from 'qs';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryOptionsApi } from '../../../shared/hooks/QueryOptionsApi';
import useQuery from '../../../shared/hooks/useQuery';
import { EXTERNAL_BILLINGS_URL } from './constants';
import EditExternalBillingItemDtoDialog from './EditExternalBillingItemDtoDialog';
import ExternalBillingItemsTable from './ExternalBillingItemsTable';
import { ExternalBillingItemDto, LicenseModuleAddon } from './types';

const ExternalBillingItemsDialog = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { agreementNumber, licensModulId } = location.state as {
        agreementNumber: number;
        licensModulId: number;
    };
    const [viewState, setViewState] = useState<'new' | 'copy' | 'edit' | 'delete' | 'none'>('none');
    const [activeExternalBillingItemDto, setActiveExternalBillingItemDto] = useState<ExternalBillingItemDto | null>(null);

    const onDialogClose = () => {
        // remove modal hash
        navigate(location.pathname);
    };

    const onEdit = (externalBillingItemDto: ExternalBillingItemDto) => {
        setViewState('edit');
        setActiveExternalBillingItemDto(externalBillingItemDto);
    };

    const onClose = () => {
        setViewState('none');
    };

    const onSubmitted = () => {
        refetchExternalBillingItems();
    };

    const showEditDialog = activeExternalBillingItemDto !== null && viewState === 'edit';

    const queryParams = {
        agreementNumber,
        licenseModuleAddOn: licensModulId,
    };
    const queryString = qs.stringify(queryParams);
    const {
        data: externalBillingItems,
        loading: externalBillingItemsLoading,
        refetch: refetchExternalBillingItems,
    } = useQuery<{ items: ExternalBillingItemDto[] }>(`${EXTERNAL_BILLINGS_URL}?${queryString}`, undefined, {
        api: QueryOptionsApi.Open,
    });

    const addonName: string = LicenseModuleAddon[licensModulId];

    return (
        <Dialog size="md" onClose={onDialogClose} defaultOpen>
            <Dialog.Content aria-label="sys page external billing items modal">
                <Dialog.Title>
                    <Trans>
                        {addonName} modules for agreement {agreementNumber}
                    </Trans>
                </Dialog.Title>

                {externalBillingItemsLoading ? (
                    <div className="h-52 w-full flex items-center justify-center ">
                        <Spinner role="spinner" />
                    </div>
                ) : (
                    <ExternalBillingItemsTable data={externalBillingItems.items} onEdit={onEdit} />
                )}

                {showEditDialog && (
                    <EditExternalBillingItemDtoDialog
                        agreementNumber={agreementNumber}
                        addonName={addonName}
                        data={activeExternalBillingItemDto}
                        onClose={onClose}
                        onSubmitted={onSubmitted}
                    />
                )}
                <Dialog.Footer>
                    <Button onClick={onDialogClose}>Close</Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};

export default ExternalBillingItemsDialog;
